// @ts-strict-ignore
import {
  Geo,
  AssetLoader,
  QueryString,
  TopLevelDomainExtractor,
  Logger,
  LoggerTransformer,
  countriesRequiringConsents,
} from "@identity/utils";
import { COOKIES, NAVIGATOR, WINDOW } from "@identity/symbols";
import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  ConsentProviderInterface,
  ConsentResolvedCallback,
} from "@identity/services";
import { USP_VALUES, UspApi } from "../usp-api";
import { SimpleTcfApi } from "../simple-tcf-api";
import { OneTrustTracker } from "./one-trust-tracker";

const ONE_TRUST_DOMAIN_IDS = {
  ".fanatical.com": "0ab0e172-dc9c-44b9-8a58-c80d9ba4fae0",
  ".futhead.com": "82c642a4-d3a9-4a78-a310-21a7b49ab17b",
  ".gamespot.com": "73588546-b116-4dbc-ab64-9db97e11fc0d",
  ".metacritic.com": "50e16f1d-a929-4cc0-80a7-3c5c303eea6a",
  ".muthead.com": "fb34a16e-696d-4748-af9f-96f3b9e2b4da",
  ".tvguide.com": "ffc22311-f4da-4e03-a1ad-32aecd522c5b",
  ".fandom.com": "dea70a1b-c82d-4fe0-86ff-5e164b0a6022",
};
const ONE_TRUST_LIBRARIES = [
  {
    url: "https://cdn.cookielaw.org/consent/tcf.stub.js",
    options: {
      charSet: "UTF-8",
    },
  },
  {
    url: "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js",
    options: {},
  },
];

@singleton()
export class OneTrust implements ConsentProviderInterface {
  private readonly trackingGroup = "C0004:1";
  private readonly testMode = !!QueryString.get("ot_test");
  private uspApiInstalled = false;
  private modalShownDuringSession = false;

  constructor(
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(WINDOW) private readonly window: Window,
    @inject(NAVIGATOR) private readonly navigator: Navigator,
    @inject(AssetLoader) private readonly assetLoader: AssetLoader,
    @inject(Geo) private readonly geo: Geo,
    @inject(UspApi) private readonly uspApi: UspApi,
    @inject(SimpleTcfApi) private readonly tcfApi: SimpleTcfApi,
    @injectWithTransform(Logger, LoggerTransformer, "OneTrust")
    private readonly logger: Logger,
    @inject(OneTrustTracker) private oneTrustTracker: OneTrustTracker,
  ) {
    this.hideDoNotSellMyInfoLink();
    this.oneTrustTracker.init();
    if (this.geo.getCountryCode() === "us") {
      this.tcfApi.install(true);
      if (this.uspApi.isUspApiAvailable()) {
        return;
      }
      this.uspApiInstalled = true;
      this.uspApi.installUspApi();
    }
    if (!this.oneTrustLoaded()) {
      this.loadOneTrustScripts();
    }
  }

  subscribe(callback: ConsentResolvedCallback) {
    const modalClosed = this.oneTrustModalClosed();
    if (modalClosed || !this.geo.isGdprCountry()) {
      this.consentsUpdated(callback);
    }
    this.initialize(callback);
  }

  private oneTrustLoaded(): boolean {
    return !!(this.window.OneTrust || this.window.OneTrustStub);
  }

  // TODO: This link should be removed from the DOM once OneTrust is fully integrated on Fandom
  private hideDoNotSellMyInfoLink(): void {
    if (this.geo.getCountryCode() !== "us") {
      return;
    }

    try {
      this.logger.info("Hiding 'Do not sell my info' link");
      const doNotSellMyInfoLink = document.querySelector(
        'a[data-tracking-label="community.usp-do-not-sell"]',
      ) as HTMLAnchorElement;
      doNotSellMyInfoLink.style.visibility = "hidden";
    } catch (e) {
      this.logger.error("Could not hide 'Do not sell my info' link", e);
    }
  }

  private oneTrustModalClosed(): boolean {
    const oneTrustCookie = this.cookiesManager.get("OptanonAlertBoxClosed");
    return oneTrustCookie && !!oneTrustCookie;
  }

  private initialize(callback: ConsentResolvedCallback): void {
    this.logger.info("Initializing OneTrust");
    const oldOptanonWrapper = this.window.OptanonWrapper;
    this.window.OptanonWrapper = () => {
      this.logger.info("OneTrust Wrapper Called");
      const modalClosed = this.oneTrustModalClosed();
      const lawRestriction =
        this.geo.isGdprCountry() || this.geo.getCountryCode() === "us";
      if (modalClosed || !lawRestriction) {
        this.consentsUpdated(callback);
        return oldOptanonWrapper?.();
      }

      this.registerImpression();
    };

    // Check if we should run Impression tracking by ourself, or it was automatically called by OneTrust
    if (
      document.getElementById("onetrust-banner-sdk") &&
      !this.modalShownDuringSession
    ) {
      this.logger.info("OneTrust Impression tracked manually");
      this.registerImpression();
    } else {
      this.logger.info("OneTrust Impression tracked automatically");
    }
  }

  private registerImpression() {
    const lawRestriction =
      this.geo.isGdprCountry() || this.geo.getCountryCode() === "us";
    if (lawRestriction && !this.modalShownDuringSession) {
      this.modalShownDuringSession = true;
      this.oneTrustTracker.sendImpressionEvent();
    }
  }

  private oneTrustCookieExists() {
    return !!this.cookiesManager.get("OptanonConsent");
  }

  private readTrackingConsents(): boolean {
    const cookie = this.cookiesManager.get("OptanonConsent");
    const groups = cookie?.split("groups=").pop()?.split(";")[0] || "";
    if (!groups.length || !cookie.includes("groups")) {
      return this.geo.getCountryCode() === "us";
    }
    return groups.includes(this.trackingGroup);
  }

  private consentsUpdated(callback: ConsentResolvedCallback): void {
    if (this.geo.isGdprCountry() && !this.oneTrustCookieExists()) {
      this.logger.info("OneTrust cookie not found");
      return;
    }

    const location = this.geo.getCountryCode();
    const trackingConsent = this.readTrackingConsents();
    if (this.modalShownDuringSession) {
      this.oneTrustTracker.sendActionEvent(
        trackingConsent ? "accept" : "reject",
      );
    } else {
      this.logger.info("OneTrust modal not shown during session");
    }

    if (location) {
      // In GDPR user gives us consent. In CCPA user gives us a signal to OptOut.
      // Therefore, in US we assume, that signal = true, means that user opted-out.
      if (this.uspApiInstalled && location === "us") {
        if (this.navigator.globalPrivacyControl) {
          this.uspApi.createPrivacyCookie(USP_VALUES.yes);
          callback({
            gdprConsent: true,
            geoRequiresConsent: false,
            geoRequiresSignal: true,
            ccpaSignal: true,
          });
          return;
        }
        this.uspApi.createPrivacyCookie(
          !trackingConsent ? USP_VALUES.yes : USP_VALUES.no,
        );
      }
      callback({
        gdprConsent: countriesRequiringConsents.includes(location)
          ? trackingConsent
          : true,
        geoRequiresConsent: countriesRequiringConsents.includes(location),
        geoRequiresSignal: location === "us",
        ccpaSignal: location === "us" ? !trackingConsent : false,
      });
    }
  }

  private loadOneTrustScripts() {
    this.logger.info("Loading OneTrust scripts");
    ONE_TRUST_LIBRARIES.forEach((library) => {
      this.assetLoader.loadScript(library.url, "text/javascript", false, null, {
        ...library.options,
        "data-domain-script": this.getDomainId(),
      });
    });
  }

  private getDomainId() {
    const tld = TopLevelDomainExtractor.extract(this.window.location.hostname);
    const domainId =
      ONE_TRUST_DOMAIN_IDS[tld] || ONE_TRUST_DOMAIN_IDS[".fandom.com"];

    return this.testMode ? `${domainId}-test` : domainId;
  }
}
